import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  loadOnboardingChecklist,
  saveOnboardingChecklist,
  ONBOARDING_ALWAYS_DONE_STEP_IDS,
  type OnboardingChecklistPersisted,
  type OnboardingStepId,
  type OnboardingStepStatus,
  countPendingSteps,
  getDefaultOnboardingChecklist,
} from 'utils/onboarding-checklist-storage';
import { ONBOARDING_STEPS, stepToHref } from 'utils/onboarding-checklist-constants';

let clientState: OnboardingChecklistPersisted = loadOnboardingChecklist();
const listeners = new Set<() => void>();

/** חייב להיות אותו reference בכל קריאה — אחרת React 18 מזהה שינוי מתמיד ב-getServerSnapshot ונכנס ללולאה */
const STABLE_SERVER_SNAPSHOT = getDefaultOnboardingChecklist();

function emit() {
  listeners.forEach((l) => l());
}

function setClientState(next: OnboardingChecklistPersisted) {
  clientState = next;
  saveOnboardingChecklist(next);
  emit();
}

function patchStepStatus(id: OnboardingStepId, status: OnboardingStepStatus) {
  if (ONBOARDING_ALWAYS_DONE_STEP_IDS.includes(id) && status !== 'done') {
    return;
  }
  setClientState({
    ...clientState,
    steps: {
      ...clientState.steps,
      [id]: status,
    },
  });
}

function patchCollapsed(collapsed: boolean) {
  setClientState({
    ...clientState,
    collapsed,
  });
}

function patchDismissed() {
  setClientState({
    ...clientState,
    dismissed: true,
  });
}

function patchResetAll() {
  setClientState(getDefaultOnboardingChecklist());
}

export function useOnboardingChecklist() {
  const state = useSyncExternalStore(
    (onStoreChange) => {
      listeners.add(onStoreChange);
      return () => listeners.delete(onStoreChange);
    },
    () => clientState,
    () => STABLE_SERVER_SNAPSHOT,
  );

  const pendingCount = useMemo(() => countPendingSteps(state.steps), [state.steps]);

  return {
    state,
    pendingCount,
    setStepStatus: useCallback((id: OnboardingStepId, status: OnboardingStepStatus) => {
      patchStepStatus(id, status);
    }, []),
    setCollapsed: useCallback((collapsed: boolean) => {
      patchCollapsed(collapsed);
    }, []),
    dismiss: useCallback(() => {
      patchDismissed();
    }, []),
    resetAll: useCallback(() => {
      patchResetAll();
    }, []),
  };
}

/** מחוץ לרכיבים (למשל תפריט עזרה): מציג את ווידג'ט האשף בפינה */
export function revealOnboardingChecklist() {
  setClientState({
    ...clientState,
    dismissed: false,
    collapsed: false,
  });
}

/**
 * פותח את הווידג'ט ומחזיר כתובת לניווט לפרק המבוקש (לקריאה עם navigate מיד אחרי).
 */
export function revealOnboardingChecklistAndGoToStep(stepId: OnboardingStepId): string {
  const step = ONBOARDING_STEPS.find((s) => s.id === stepId);
  const href = step ? stepToHref(step) : '/chats';
  setClientState({
    ...clientState,
    dismissed: false,
    collapsed: false,
  });
  return href;
}

/** השלב הראשון שעדיין pending — לפי סדר האשף */
export function getFirstPendingOnboardingStepId(
  steps: Record<OnboardingStepId, OnboardingStepStatus>,
): OnboardingStepId | null {
  const step = ONBOARDING_STEPS.find((s) => steps[s.id] === 'pending');
  return step?.id ?? null;
}

export function isOnboardingStepActive(
  stepId: OnboardingStepId,
  steps: Record<OnboardingStepId, OnboardingStepStatus>,
): boolean {
  return getFirstPendingOnboardingStepId(steps) === stepId;
}
